// src/pages/operations-dashboard/OperationsDashboard.jsx
import React, { useState, useRef, useEffect } from "react";
import {
  LayoutDashboard,
  Package,
  Truck,
  IndianRupee,
  AlertTriangle,
  RotateCcw,
  MapPin,
  ChevronRight,
  Sparkles,
} from "lucide-react";
import useDashboardData from "../../hooks/useDashboardData";
import DashboardFilterBar from "./components/DashboardFilterBar";
import KpiCards from "./components/KpiCards";
import ShipmentsOverviewSection from "./components/ShipmentsOverviewSection";
import ShipmentStatusSection from "./components/ShipmentStatusSection";
import CourierSplitSection from "./components/CourierSplitSection";
import DeliveryPerformanceSection from "./components/DeliveryPerformanceSection";
import RevenueSection from "./components/RevenueSection";
import NdrCodSection from "./components/NdrCodSection";
import AttemptAnalyticsSection from "./components/AttemptAnalyticsSection";
import ZoneWiseSection from "./components/ZoneWiseSection";
import { PERIOD_OPTIONS } from "../../utils/dashboardConstants";

/**
 * Navigation tab definitions for the dashboard sections.
 */
const TABS = [
  { key: "overview", label: "Overview", icon: LayoutDashboard, color: "text-blue-500", bg: "bg-blue-50" },
  { key: "shipments", label: "Shipments", icon: Package, color: "text-violet-500", bg: "bg-violet-50" },
  { key: "delivery", label: "Delivery", icon: Truck, color: "text-emerald-500", bg: "bg-emerald-50" },
  { key: "revenue", label: "Revenue", icon: IndianRupee, color: "text-teal-500", bg: "bg-teal-50" },
  { key: "ndr-cod", label: "NDR & COD", icon: AlertTriangle, color: "text-amber-500", bg: "bg-amber-50" },
  { key: "attempts", label: "Attempts", icon: RotateCcw, color: "text-indigo-500", bg: "bg-indigo-50" },
  { key: "zones", label: "Zones", icon: MapPin, color: "text-pink-500", bg: "bg-pink-50" },
];

/**
 * Full-page skeleton for initial load.
 */
const FullPageSkeleton = () => (
  <div className="space-y-6 p-6 animate-pulse">
    {/* KPI Cards Skeleton */}
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
        <div key={i} className="bg-white rounded-2xl border border-gray-100 p-5 space-y-3">
          <div className="flex justify-between">
            <div className="w-10 h-10 bg-gray-100 rounded-xl" />
            <div className="w-16 h-6 bg-gray-100 rounded-full" />
          </div>
          <div className="h-8 bg-gray-100 rounded-lg w-28" />
          <div className="h-4 bg-gray-50 rounded w-20" />
        </div>
      ))}
    </div>
    {/* Chart Skeletons */}
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <div className="lg:col-span-2 bg-white rounded-2xl border border-gray-100 p-6">
        <div className="h-6 w-48 bg-gray-100 rounded mb-6" />
        <div className="h-64 bg-gray-50 rounded-xl" />
      </div>
      <div className="bg-white rounded-2xl border border-gray-100 p-6 space-y-4">
        <div className="h-6 w-32 bg-gray-100 rounded mb-4" />
        {[1, 2, 3].map((i) => <div key={i} className="h-16 bg-gray-50 rounded-xl" />)}
      </div>
    </div>
    <div className="bg-white rounded-2xl border border-gray-100 p-6">
      <div className="h-6 w-56 bg-gray-100 rounded mb-6" />
      <div className="h-80 bg-gray-50 rounded-xl" />
    </div>
  </div>
);

/**
 * Empty state illustration.
 */
const EmptyState = ({ section }) => (
  <div className="flex flex-col items-center justify-center py-16 text-center">
    <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mb-4">
      <Package className="w-10 h-10 text-gray-300" />
    </div>
    <h3 className="text-sm font-semibold text-gray-600 mb-1">No data available</h3>
    <p className="text-xs text-gray-400 max-w-xs">
      There's no {section} data for the selected period. Try changing the date range or check back later.
    </p>
  </div>
);

export default function OperationsDashboard() {
  const [activeTab, setActiveTab] = useState("overview");
  const sectionRefs = useRef({});
  const tabsContainerRef = useRef(null);

  const {
    period,
    setPeriod,
    fromDateTime,
    setFromDateTime,
    toDateTime,
    setToDateTime,
    summary,
    shipmentsOverview,
    shipmentsStatus,
    shipmentsZoneWise,
    courierSplit,
    deliveryPerformance,
    revenue,
    ndr,
    cod,
    attemptAnalytics,
    loading,
    isAnyLoading,
    isInitialLoad,
    lastRefresh,
    refresh,
  } = useDashboardData();

  // Scroll-spy: update active tab as user scrolls
  useEffect(() => {
    if (activeTab !== "overview") return; // Only for "all sections" view

    const observer = new IntersectionObserver(
      (entries) => {
        // No-op — we keep the overview tab focused
      },
      { rootMargin: "-100px 0px -60% 0px" }
    );

    return () => observer.disconnect();
  }, [activeTab]);

  const periodLabel = PERIOD_OPTIONS.find((p) => p.value === period)?.label || period;

  // Render content based on active tab
  const renderContent = () => {
    switch (activeTab) {
      case "overview":
        return (
          <div className="space-y-6">
            {/* KPI Cards — Always visible */}
            <KpiCards summary={summary} loading={loading} />

            {/* Shipment Overview */}
            <ShipmentsOverviewSection data={shipmentsOverview} loading={loading} />

            {/* Shipment Status */}
            <ShipmentStatusSection data={shipmentsStatus} loading={loading} />

            {/* Courier Split & Zone-wise side by side on XL */}
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
              <CourierSplitSection data={courierSplit} loading={loading} />
              <ZoneWiseSection data={shipmentsZoneWise} loading={loading} />
            </div>

            {/* Delivery Performance */}
            <DeliveryPerformanceSection data={deliveryPerformance} loading={loading} />

            {/* Revenue */}
            <RevenueSection data={revenue} summary={summary} loading={loading} />

            {/* NDR & COD */}
            <NdrCodSection ndrData={ndr} codData={cod} loading={loading} />

            {/* Attempt Analytics */}
            <AttemptAnalyticsSection data={attemptAnalytics} loading={loading} />
          </div>
        );

      case "shipments":
        return (
          <div className="space-y-6">
            <KpiCards summary={summary} loading={loading} />
            <ShipmentsOverviewSection data={shipmentsOverview} loading={loading} />
            <ShipmentStatusSection data={shipmentsStatus} loading={loading} />
            <CourierSplitSection data={courierSplit} loading={loading} />
            <ZoneWiseSection data={shipmentsZoneWise} loading={loading} />
          </div>
        );

      case "delivery":
        return (
          <div className="space-y-6">
            <DeliveryPerformanceSection data={deliveryPerformance} loading={loading} />
            <AttemptAnalyticsSection data={attemptAnalytics} loading={loading} />
          </div>
        );

      case "revenue":
        return <RevenueSection data={revenue} summary={summary} loading={loading} />;

      case "ndr-cod":
        return <NdrCodSection ndrData={ndr} codData={cod} loading={loading} />;

      case "attempts":
        return <AttemptAnalyticsSection data={attemptAnalytics} loading={loading} />;

      case "zones":
        return <ZoneWiseSection data={shipmentsZoneWise} loading={loading} />;

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-slate-50 to-gray-50">
      {/* ─── Page Header ──────────────────────────── */}
      <div className="bg-white border-b border-gray-100">
        <div className="px-6 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 shadow-lg shadow-blue-200/50">
                <LayoutDashboard className="w-5.5 h-5.5 text-white" strokeWidth={2} />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900 tracking-tight">Operations Dashboard</h1>
                <p className="text-xs text-gray-400 mt-0.5 flex items-center gap-1.5">
                  <Sparkles className="w-3 h-3 text-amber-400" />
                  Real-time logistics intelligence
                  <span className="mx-1.5 text-gray-300">·</span>
                  <span className="font-medium text-gray-500">{periodLabel}</span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ─── Filter Bar ───────────────────────────── */}
      <DashboardFilterBar
        period={period}
        setPeriod={setPeriod}
        fromDateTime={fromDateTime}
        setFromDateTime={setFromDateTime}
        toDateTime={toDateTime}
        setToDateTime={setToDateTime}
        onRefresh={refresh}
        lastRefresh={lastRefresh}
        isLoading={isAnyLoading}
      />

      {/* ─── Tab Navigation ───────────────────────── */}
      <div className="bg-white border-b border-gray-100 shadow-sm">
        <div className="px-6">
          <div
            ref={tabsContainerRef}
            className="flex items-center gap-1 overflow-x-auto scrollbar-hide py-2 -mb-px"
          >
            {TABS.map((tab) => {
              const isActive = activeTab === tab.key;
              const Icon = tab.icon;
              return (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium whitespace-nowrap transition-all duration-200 ${
                    isActive
                      ? `${tab.bg} ${tab.color} shadow-sm font-semibold`
                      : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  <Icon className="w-4 h-4" strokeWidth={isActive ? 2.5 : 2} />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* ─── Dashboard Content ────────────────────── */}
      <div className="p-6">
        {isInitialLoad ? <FullPageSkeleton /> : renderContent()}
      </div>

      {/* ─── Floating Breadcrumb (shows active section context) ── */}
      {activeTab !== "overview" && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-30">
          <div className="flex items-center gap-2 px-4 py-2.5 bg-white/90 backdrop-blur-lg border border-gray-200 rounded-full shadow-2xl shadow-gray-200/50">
            <button
              onClick={() => setActiveTab("overview")}
              className="text-xs font-medium text-gray-400 hover:text-blue-500 transition-colors"
            >
              Overview
            </button>
            <ChevronRight className="w-3 h-3 text-gray-300" />
            <span className="text-xs font-semibold text-gray-700">
              {TABS.find((t) => t.key === activeTab)?.label}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
